/**
 * Wire types for EI guided reporting (docs/specs/018-ei-guided-reporting.md):
 * the conversation state the BFF owns and the view model it returns to the
 * MFE on every turn. The BFF imports these with `import type` only, so nothing
 * from the lib's main entry (which also exports the browser-only HTTP
 * client) loads in Node. No subpath mapping: Native Federation shares every
 * tsconfig path mapping, and a second one here made it misattribute the
 * barrel's files.
 *
 * The server is authoritative: the browser sends one event per turn and
 * renders whatever comes back. Nothing here is computed in the browser.
 *
 * All copy travels as content-key references (`GuidedText`), never as
 * rendered strings: the MFE resolves each key under
 * `employment-insurance.guided-reporting.` and formats every date and amount
 * parameter in the active locale. No date literal appears in any key.
 */

export type GuidedSectionId = 'absences' | 'work' | 'fullTimeJob' | 'training' | 'availability' | 'otherMoney';

export const GUIDED_SECTIONS: readonly GuidedSectionId[] = [
  'absences',
  'work',
  'fullTimeJob',
  'training',
  'availability',
  'otherMoney',
];

export type YesNo = 'yes' | 'no';

export type IntroChoice = 'nothingChanged' | 'someChanged' | 'notSure';

export type TravelReasonCode =
  | 'vacation'
  | 'accompanyMedical'
  | 'funeral'
  | 'approvedProgram'
  | 'jobInterview'
  | 'lookForWork'
  | 'medicalTreatment'
  | 'visitIll'
  | 'other';

export type StopReasonCode = 'shortage' | 'quit' | 'dismissed' | 'other';

/** A single day's reason for not being available. */
export type AvailDayReason = 'illness' | 'outsideCanada' | 'other';

export type AvailReasonCode = AvailDayReason | 'different';

export type OtherMoneyType = 'vacationPay' | 'severance' | 'bonus' | 'pension' | 'other';

export interface WeekEarnings {
  hours: number;
  /** Gross earnings before deductions, whole dollars. */
  earnings: number;
  selfEmployed: boolean;
  /** Only meaningful when selfEmployed. */
  selfEmploymentEarnings: number;
}

export interface TrainingWeek {
  hours: number;
  allowance: number;
}

export interface OtherMoneyItem {
  type: OtherMoneyType;
  /** The claimant's own words, for type `other`. */
  description?: string;
  from: string;
  amount: number;
}

/**
 * Everything the claimant answered, as stored on the submitted report.
 * Dates are UTC calendar dates (`YYYY-MM-DD`), times 24-hour `HH:MM`.
 */
export interface GuidedAnswers {
  travel?: YesNo;
  travelOver24?: YesNo;
  travelLeftDate?: string;
  travelLeftTime?: string;
  travelBackDate?: string;
  travelBackTime?: string;
  travelReason?: TravelReasonCode;
  travelReasonOther?: string;

  worked?: YesNo;
  week1?: WeekEarnings;
  week2?: WeekEarnings;
  employerPhones?: string[];
  stopped?: YesNo;
  stopReason?: StopReasonCode;
  stopReasonOther?: string;

  returnedFullTime?: YesNo;
  fullTimeJobDate?: string;

  training?: YesNo;
  trainingWeek1?: TrainingWeek;
  trainingWeek2?: TrainingWeek;
  trainingAllowanceFrom?: string;

  availableEachDay?: YesNo;
  /** Weekdays (ISO dates) the claimant wasn't available. */
  availDates?: string[];
  availReason?: AvailReasonCode;
  /** Per-day reasons; one entry per availDates day. */
  availReasons?: Record<string, AvailDayReason>;
  /** Recorded only through the "Illness or injury" availability reason. */
  sick?: YesNo;
  wliWeek1?: YesNo;
  wliWeek1Amount?: number;
  wliWeek2?: YesNo;
  wliWeek2Amount?: number;

  otherMoney?: YesNo;
  otherMoneyItems?: OtherMoneyItem[];
  /** Asked for a Pension item only. */
  pensionChanged?: YesNo;
}

// ── Copy references ────────────────────────────────────────────────────────

export type GuidedParam =
  | { t: 'text'; v: string }
  | { t: 'number'; v: number }
  | { t: 'money'; v: number }
  /** A UTC calendar date, `YYYY-MM-DD`, formatted long ("April 7, 2025"). */
  | { t: 'date'; v: string }
  /** A UTC calendar date, formatted short ("Apr 7"). */
  | { t: 'shortDate'; v: string }
  /** Weekday ISO dates shown as a span: first–last, or the single day. */
  | { t: 'daySpan'; v: string[] }
  /** Weekday ISO dates shown as a comma-separated list. */
  | { t: 'dayList'; v: string[] }
  // Adding a param type: the MFE must reach users before the BFF sends it. An
  // MFE built without the type has no case for it and renders "undefined" (or
  // a broken placeholder) in the sentence. Only MFEs built after the
  // resolver's default case show the param's plain `v` instead, so that
  // fallback covers types added after it, not `time` and `phone`. Nothing
  // enforces the order: the MFE and BFF deploy in one Helm release, so ship
  // the MFE half in its own release first, then the BFF half.
  /** A 24-hour wall-clock time, `HH:MM`, with no time zone ("08:00" / « 8 h »). */
  | { t: 'time'; v: string }
  /**
   * A phone number as stored: 10 digits, or a value saved before phones were
   * stored as digits (`(416) 555-0123`). Formatted per locale at render.
   */
  | { t: 'phone'; v: string }
  /** An inclusive UTC calendar-date range, `from`–`to`. */
  | { t: 'range'; from: string; to: string }
  /** Nested copy. */
  | { t: 'texts'; v: GuidedText[]; join: 'comma' | 'and' | 'semicolon' | 'dot' };

export interface GuidedText {
  /** Relative to `employment-insurance.guided-reporting.`. */
  key: string;
  params?: Record<string, GuidedParam>;
}

// ── Transcript ─────────────────────────────────────────────────────────────

export type GuidedStepId =
  | 'intro'
  | 'travel'
  | 'travelOver24'
  | 'travelDates'
  | 'travelReason'
  | 'work'
  | 'earnWeek1'
  | 'earnWeek2'
  | 'employerPhones'
  | 'stop'
  | 'stopReason'
  | 'stopReasonOther'
  | 'fullTimeJob'
  | 'fullTimeJobDate'
  | 'training'
  | 'trainingWeek1'
  | 'trainingWeek2'
  | 'trainingAllowanceFrom'
  | 'avail'
  | 'availDates'
  | 'availReason'
  | 'availReasonPerDay'
  | 'wliWeek1'
  | 'wliWeek1Amount'
  | 'wliWeek2'
  | 'wliWeek2Amount'
  | 'other'
  | 'otherType'
  | 'otherDetails'
  | 'pension';

export type GuidedTranscriptEvent =
  /** `stepId: 'flag'` is a flag's own question ("Would you like to review…?"). */
  | { id: number; kind: 'question'; stepId: GuidedStepId | 'flag'; section: number; text: GuidedText }
  | { id: number; kind: 'answer'; text: GuidedText }
  | { id: number; kind: 'agent'; text: GuidedText }
  | { id: number; kind: 'note'; text: GuidedText }
  | { id: number; kind: 'error'; text: GuidedText }
  | {
      id: number;
      kind: 'flag';
      code: GuidedFlagCode;
      headline: GuidedText;
      detail: GuidedText;
      action?: GuidedText;
    };

// ── Flags ──────────────────────────────────────────────────────────────────

export type GuidedFlagCode =
  | 'STALE_TRAVEL'
  | 'STALE_AVAILABILITY'
  | 'REASON_MISMATCH'
  | 'SE_MWIE_WEEK1'
  | 'SE_MWIE_WEEK2'
  | 'SE_MWIE_COMBINED'
  | 'TRAINING_WLI_WEEK1'
  | 'TRAINING_WLI_WEEK2'
  | 'FTJOB_CASCADE_TRAINING'
  | 'FTJOB_CASCADE_AVAILABILITY'
  | 'FTJOB_CASCADE_OTHERMONEY'
  | 'PENSION_CHANGE_VS_CLAIM'
  | 'EARNINGS_NO_HOURS_CLASSIFICATION'
  | 'FULL_TIME_AFTER_NO_WORK'
  | 'HOURS_NO_EARNINGS'
  | 'EARNINGS_NO_HOURS'
  | 'HOURS_OVER_CEILING';

/** A flag raised during the conversation, kept on the submitted report. */
export interface GuidedRaisedFlag {
  code: GuidedFlagCode;
  /** The figures that produced it, as computed on the server. */
  figures: Record<string, number | string | string[] | null>;
  raisedAt: string;
  /** The claimant's response, when the flag offered one. */
  response?: string;
}

// ── The turn interface ─────────────────────────────────────────────────────

export type GuidedEvent =
  | { type: 'answer'; stepId: GuidedStepId; value: unknown }
  | { type: 'back' }
  | { type: 'flagResponse'; choice: string }
  | { type: 'change'; section: GuidedSectionId | 'intro' }
  | { type: 'attest'; confirmed: boolean };

export interface GuidedChoiceOption {
  value: string;
  text: GuidedText;
  /** The prototype's secondary ("alt") chip style. */
  secondary?: boolean;
}

/**
 * The input under the current question. `values` pre-fills a form with the
 * answers already held (Back, Change and carry-forward keep them).
 */
export type GuidedInput =
  | { kind: 'choice'; options: GuidedChoiceOption[]; selected?: string }
  | { kind: 'flag'; options: GuidedChoiceOption[] }
  | { kind: 'travelDates'; values: Partial<GuidedAnswers> }
  | { kind: 'travelReason'; options: GuidedChoiceOption[]; values: Partial<GuidedAnswers> }
  | { kind: 'week'; week: 1 | 2; values: Partial<WeekEarnings> }
  | { kind: 'employerPhones'; values: string[] }
  | { kind: 'text'; field: 'stopReasonOther' | 'trainingAllowanceFrom'; value: string }
  | { kind: 'date'; value: string }
  | { kind: 'trainingWeek'; week: 1 | 2; values: Partial<TrainingWeek> }
  | { kind: 'weekdays'; days: string[]; selected: string[] }
  | { kind: 'perDayReasons'; days: string[]; values: Record<string, AvailDayReason> }
  | { kind: 'amount'; week: 1 | 2; value: number | null }
  | { kind: 'otherTypes'; selected: OtherMoneyType[]; otherDescription: string }
  | { kind: 'otherDetails'; index: number; count: number; values: Partial<OtherMoneyItem> };

export interface GuidedPeriodInfo {
  /** The schedule's period, as in `periods[]` (ISO timestamps, end exclusive). */
  periodStart: string;
  periodEnd: string;
  /** UTC calendar dates, inclusive. */
  firstDay: string;
  lastDay: string;
  week1: { from: string; to: string };
  week2: { from: string; to: string };
  /** The period's Monday-to-Friday dates. */
  weekdays: string[];
}

export interface GuidedProgressSection {
  section: GuidedSectionId;
  number: number;
  status: 'done' | 'current' | 'todo';
  /** One-line answer summary, once answered. */
  summary?: GuidedText;
}

export interface GuidedReviewRow {
  question: GuidedText;
  answer: GuidedText;
}

export interface GuidedReviewSection {
  section: GuidedSectionId;
  number: number;
  rows: GuidedReviewRow[];
}

export interface GuidedReview {
  /** Set when "Nothing's changed" carried the answers forward. */
  prefillSource?: 'lastReport' | 'application';
  intro: GuidedReviewRow;
  sections: GuidedReviewSection[];
  attested: boolean;
}

export interface GuidedConversationView {
  period: GuidedPeriodInfo;
  phase: 'conversation' | 'review';
  transcript: GuidedTranscriptEvent[];
  /** The question being asked (conversation phase only). */
  current: { stepId: GuidedStepId | 'flag'; section: number; input: GuidedInput } | null;
  /** Set while a Change re-entry is in progress. */
  editing: GuidedSectionId | 'intro' | null;
  canGoBack: boolean;
  progress: GuidedProgressSection[];
  review: GuidedReview | null;
  /** All six sections answered and the attestation confirmed. */
  canSubmit: boolean;
}

/** Recorded on the report: what was attested to, and when. */
export interface GuidedAttestation {
  statementsVersion: string;
  attestedAt: string;
}
